/**
 * src/components/shared/ContentLangToggle.jsx — TICKET-959.
 * Segmented Original / EN / ES toggle for production content. The choice is shared by
 * every screen on this device (useContentLanguage). `translating` shows a quiet status.
 */
import Button from './Button';
import { useContentLanguage, CONTENT_LANGS } from '../../lib/i18n/useContentLanguage';
import { useT } from '../../lib/i18n/I18nContext';

const LABEL_KEYS = { orig: 'contentLang.original', en: 'contentLang.en', es: 'contentLang.es' };
const FALLBACK = { orig: 'Original', en: 'EN', es: 'ES' };

// t(key) only (no options object): some test mocks return the 2nd argument, and a
// label must always be a string. Unresolved keys fall back to the built-in label.
function label(t, key, fallback) {
  const v = typeof t === 'function' ? t(key) : null;
  return typeof v === 'string' && v && v !== key ? v : fallback;
}

export default function ContentLangToggle({ translating = false, error = null, testIdPrefix = 'content-lang' }) {
  const { lang, setLang } = useContentLanguage();
  const { t } = useT();
  return (
    <div data-testid={`${testIdPrefix}-toggle`} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2, 8px)', flexWrap: 'wrap' }}>
      <div role="group" aria-label={label(t, 'contentLang.label', 'Content language')} style={{ display: 'flex', gap: 4 }}>
        {CONTENT_LANGS.map((l) => (
          <Button key={l} variant="toggle" size="sm" active={lang === l} aria-pressed={lang === l}
            data-testid={`${testIdPrefix}-${l}`} onClick={() => setLang(l)}>
            {label(t, LABEL_KEYS[l], FALLBACK[l])}
          </Button>
        ))}
      </div>
      {translating && (
        <span data-testid={`${testIdPrefix}-status`} style={{ fontSize: 'var(--fs-12)', color: 'var(--t2)' }}>
          {label(t, 'contentLang.translating', 'Translating…')}
        </span>
      )}
      {!translating && error && (
        <span data-testid={`${testIdPrefix}-status`} style={{ fontSize: 'var(--fs-12)', color: 'var(--t2)' }}>
          {label(t, 'contentLang.paused', 'Translation unavailable — showing the original')}
        </span>
      )}
    </div>
  );
}
